import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';

import dayjs from 'dayjs';

import {
  Modal,
  NumberStepper,
  Button,
  CustomSelect,
  DatePickerCustom,
  Pressable,
  Radio,
  RadioGroup,
} from '../../../shared/ui';

const INTERVAL_UNIT_OPTIONS = ['день', 'неделя', 'месяц'] as const;
const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const; // 1–7 = Пн–Вс

/** Соответствие кнопкам Пн–Вс: 1=Пн … 7=Вс (getDay(): 0=Вс → 7). */
function getLocalWeekdayOneToSeven(): number {
  const d = new Date().getDay();
  return d === 0 ? 7 : d;
}

export type RecurrenceState = {
  interval: number;
  unit: 'day' | 'week' | 'month';
  weekdays: number[];
  /** День месяца для unit === 'month' (1–31). */
  monthDay?: number;
  endType: 'never' | 'until_date' | 'after_count';
  endDate?: string;
  endCount?: number;
};

const getDefaultRecurrence = (): RecurrenceState => ({
  interval: 1,
  unit: 'week',
  weekdays: [getLocalWeekdayOneToSeven()],
  monthDay: 1,
  endType: 'never',
  endDate: undefined,
  endCount: 12,
});

const unitToOption: Record<RecurrenceState['unit'], string> = {
  day: 'день',
  week: 'неделя',
  month: 'месяц',
};
const optionToUnit = { день: 'day' as const, неделя: 'week' as const, месяц: 'month' as const };

type RecurrenceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialRecurrence?: RecurrenceState | null;
  onDone: (recurrence: RecurrenceState) => void;
};

export const RecurrenceModal: FC<RecurrenceModalProps> = ({ isOpen, onClose, initialRecurrence, onDone }) => {
  const [state, setState] = useState<RecurrenceState>(() => initialRecurrence ?? getDefaultRecurrence());

  useEffect(() => {
    if (isOpen) {
      setState(initialRecurrence ?? getDefaultRecurrence());
    }
  }, [isOpen, initialRecurrence]);

  const setIntervalVal = (value: number) => {
    const n = Math.max(1, Math.min(99, value));
    setState((s) => ({ ...s, interval: n }));
  };

  const setMonthDayVal = (value: number) => {
    const n = Math.max(1, Math.min(31, value));
    setState((s) => ({ ...s, monthDay: n }));
  };

  const toggleWeekday = (day: number) => {
    setState((s) => {
      const next = s.weekdays.includes(day)
        ? s.weekdays.filter((d) => d !== day)
        : [...s.weekdays, day].sort((a, b) => a - b);
      return { ...s, weekdays: next };
    });
  };

  const handleDone = () => {
    onDone(state);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Повторение встречи"
      closeOnEscape={false}
      footer={
        <>
          <Button size="lg" variant="neutral" onClick={onClose}>
            Отменить
          </Button>
          <Button size="lg" variant="dark" onClick={handleDone}>
            Готово
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Повторять с интервалом */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 flex-nowrap">
            <p className="text-body-l text-ink whitespace-nowrap shrink-0">Повторять с интервалом</p>
            <NumberStepper label="Интервал повторения" value={state.interval} onChange={setIntervalVal} />
            <CustomSelect
              selected={unitToOption[state.unit]}
              items={[...INTERVAL_UNIT_OPTIONS]}
              onChange={(val) => setState((s) => ({ ...s, unit: optionToUnit[val as keyof typeof optionToUnit] }))}
              size="md"
              readOnly
              clickToOpenClose
              showError={false}
              className="w-36"
            />
          </div>

          {/* День месяца (только для месяца) */}
          {state.unit === 'month' && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3 flex-nowrap">
                <p className="text-body-l text-ink whitespace-nowrap shrink-0">День месяца</p>
                <NumberStepper label="День месяца" max={31} value={state.monthDay ?? 1} onChange={setMonthDayVal} />
              </div>
              {(state.monthDay ?? 1) === 31 && (
                <p className="text-ui-xs text-danger">Встреча добавится в каждый последний день месяца.</p>
              )}
            </div>
          )}
        </div>

        {/* Дни повторения (только для недели) */}
        {state.unit === 'week' && (
          <div className="flex flex-col gap-2">
            <p className="text-body-l text-ink">Дни повторения</p>
            <div className="inline-flex w-[490px] max-w-full border border-line rounded-card overflow-hidden">
              {(WEEKDAY_LABELS as unknown as string[]).map((label, index) => {
                const day = index + 1;
                const isSelected = state.weekdays.includes(day);
                const isFirst = index === 0;
                const isLast = index === 6;
                return (
                  <Pressable
                    key={day}
                    aria-pressed={isSelected}
                    onClick={() => toggleWeekday(day)}
                    className={classNames(
                      'h-[40px] min-w-[70px] w-[70px] flex items-center justify-center transition-colors text-body-l-strong relative flex-shrink-0 cursor-pointer',
                      isFirst && 'rounded-l-card',
                      isLast && 'rounded-r-card',
                      isSelected ? 'bg-primary text-white' : 'bg-white text-ink-muted',
                      index < 6 && 'border-r border-line',
                    )}
                    style={{
                      ...(isFirst && { borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px' }),
                      ...(isLast && { borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }),
                    }}
                  >
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                      {label}
                    </span>
                    {isSelected && (
                      <svg
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 flex-shrink-0 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </Pressable>
                );
              })}
            </div>
          </div>
        )}

        <RadioGroup<RecurrenceState['endType']>
          label="Окончание"
          value={state.endType}
          onChange={(endType) =>
            setState((s) => ({
              ...s,
              endType,
              endDate: endType === 'until_date' ? s.endDate || dayjs().add(1, 'month').format('YYYY-MM-DD') : s.endDate,
            }))
          }
        >
          <Radio value="never" label="Никогда" />
          <div className="flex flex-wrap items-center gap-3">
            <Radio value="until_date" label="Дата" />
            {state.endType === 'until_date' && (
              <div className="w-full sm:w-auto">
                <DatePickerCustom
                  date={state.endDate || dayjs().add(1, 'month').format('YYYY-MM-DD')}
                  size="md"
                  setDate={(d) => setState((s) => ({ ...s, endDate: d }))}
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Radio value="after_count" label="После" />
            {state.endType === 'after_count' && (
              <div className="flex items-center gap-2">
                <NumberStepper
                  label="Количество повторений"
                  value={state.endCount ?? 12}
                  onChange={(n) => setState((s) => ({ ...s, endCount: n }))}
                />
                <span className="text-body-l text-ink-muted">повторов</span>
              </div>
            )}
          </div>
        </RadioGroup>
      </div>
    </Modal>
  );
};
